import { useEffect, useMemo, useRef, useState } from "react";
import { FaPlay } from "react-icons/fa6";
import { LiaCheckDoubleSolid } from "react-icons/lia";
import { Message } from "../../../types/MessageListType";
import {
  chatMessageBubbleMosaicLayout,
  mosaicCornerRadii,
} from "../../../utils/chatMessageBubbleMosaicLayout";
import {
  resolveMediaCaption,
  resolveMediaSrc,
} from "../../../utils/mediaMessageUrl";
import { resolveMediaDims } from "../../../utils/mediaDims";
import { useAppDispatch } from "../../../utils/hooks";
import { setViewImage } from "../../../store/Slices/ViewManagerSlice";
import { formatTimeOnly } from "../../../utils/formatUTCtoLocalDate";
import { useTimeFormatOptions } from "../../../hooks/useTimeFormatOptions";
import { blurHashToDataUrl } from "../../../utils/blurHashPaint";
import TgMediaProgressRing from "../../../components/TgMediaProgressRing";

const MOSAIC_MAX = { width: 300, height: 300 };

type Props = {
  messages: Message[];
  isOwn: boolean;
  theme: string;
};

function pickBlurHoldMs() {
  return 40 + Math.floor(Math.random() * (1000 - 40 + 1));
}

function MosaicCell({
  msg,
  frame,
  radius,
  onOpen,
}: {
  msg: Message;
  frame: { x: number; y: number; width: number; height: number };
  radius: string;
  onOpen: () => void;
}) {
  const isVideo = msg.message_type === "video";
  const src = isVideo
    ? msg.message_thumbnail || resolveMediaSrc(msg)
    : resolveMediaSrc(msg);
  const blurHash = msg.blur_hash || msg.blurHash || null;
  const preview =
    msg.message_preview ||
    (isVideo ? msg.message_thumbnail : "") ||
    "";

  const blurUrl = useMemo(
    () => (blurHash ? blurHashToDataUrl(blurHash, 32, 32) : null),
    [blurHash],
  );
  const lqipUrl =
    !blurUrl && preview && preview !== src ? String(preview) : "";

  const [decoded, setDecoded] = useState(false);
  const [holdDone, setHoldDone] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [keepPlaceholder, setKeepPlaceholder] = useState(true);
  const [keepRing, setKeepRing] = useState(true);
  const [loadKey, setLoadKey] = useState(0);
  const holdMsRef = useRef(pickBlurHoldMs());

  useEffect(() => {
    holdMsRef.current = pickBlurHoldMs();
    setDecoded(false);
    setHoldDone(false);
    setRevealed(false);
    setKeepPlaceholder(true);
    setKeepRing(true);
    const t = window.setTimeout(() => setHoldDone(true), holdMsRef.current);
    return () => window.clearTimeout(t);
  }, [src, blurHash, loadKey]);

  useEffect(() => {
    if (decoded && holdDone) setRevealed(true);
  }, [decoded, holdDone]);

  useEffect(() => {
    if (!revealed) return;
    const t = window.setTimeout(() => setKeepPlaceholder(false), 320);
    const t2 = window.setTimeout(() => setKeepRing(false), 420);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(t2);
    };
  }, [revealed]);

  const showPlaceholder =
    keepPlaceholder && Boolean(blurUrl || lqipUrl);
  const showRing = keepRing && !revealed;

  return (
    <button
      type="button"
      className="absolute block overflow-hidden bg-[#2a2a2a] p-0 dark:bg-[#1f1f1f]"
      style={{
        left: frame.x,
        top: frame.y,
        width: frame.width,
        height: frame.height,
        borderRadius: radius,
      }}
      onClick={onOpen}
    >
      {showPlaceholder && blurUrl ? (
        <img
          src={blurUrl}
          alt=""
          draggable={false}
          aria-hidden
          decoding="async"
          className="absolute inset-0 h-full w-full select-none object-cover"
          style={{
            opacity: revealed ? 0 : 1,
            filter: "blur(10px)",
            transform: "scale(1.08)",
            transition: "opacity 240ms ease",
          }}
        />
      ) : null}
      {showPlaceholder && !blurUrl && lqipUrl ? (
        <img
          src={lqipUrl}
          alt=""
          draggable={false}
          aria-hidden
          decoding="async"
          className="absolute inset-0 h-full w-full select-none object-cover"
          style={{
            opacity: revealed ? 0 : 1,
            filter: "blur(8px)",
            transform: "scale(1.05)",
            transition: "opacity 220ms ease",
          }}
        />
      ) : null}

      {src ? (
        <img
          key={loadKey}
          src={src}
          alt=""
          draggable={false}
          decoding="async"
          className="absolute inset-0 h-full w-full select-none object-cover"
          style={{
            opacity: blurUrl || lqipUrl ? (revealed ? 1 : 0) : revealed ? 1 : 0.01,
            transition: "opacity 280ms ease",
          }}
          onLoad={() => setDecoded(true)}
          onError={() => {
            setDecoded(true);
            setLoadKey((k) => k);
          }}
        />
      ) : null}

      {showRing ? (
        <span className="pointer-events-none absolute inset-0 grid place-content-center">
          <TgMediaProgressRing
            active={!revealed}
            done={revealed}
            size={Math.min(40, Math.max(28, frame.width * 0.28))}
          />
        </span>
      ) : null}

      {isVideo && revealed ? (
        <span className="pointer-events-none absolute inset-0 grid place-content-center">
          <span className="rounded-full bg-black/50 p-2 text-white">
            <FaPlay className="h-3 w-3" />
          </span>
        </span>
      ) : null}
    </button>
  );
}

export default function MediaAlbumBubble({ messages, isOwn, theme }: Props) {
  const dispatch = useAppDispatch();
  const { hour12 } = useTimeFormatOptions();
  const last = messages[messages.length - 1];
  const caption =
    messages.map(resolveMediaCaption).find((c) => c.length > 0) || "";

  const sizes = messages.map((m) => {
    const d = resolveMediaDims(m);
    return {
      width: d?.w || 256,
      height: d?.h || 256,
    };
  });

  const layout = chatMessageBubbleMosaicLayout(MOSAIC_MAX, sizes, 1);
  const urls = messages.map((m) => resolveMediaSrc(m)).filter(Boolean);

  const openAt = (index: number) => {
    if (!urls.length) return;
    dispatch(
      setViewImage({
        show_image: true,
        image_src: urls,
        currentIndex: Math.max(0, Math.min(index, urls.length - 1)),
      }),
    );
  };

  return (
    <div
      className={`relative max-w-[300px] overflow-hidden ${
        caption
          ? `rounded-lg p-px ${isOwn ? "bg-primary" : "bg-otherMessageBg"}`
          : ""
      }`}
      data-message-id={last.message_id}
      id={String(last.message_id)}
    >
      <div
        className={`relative overflow-hidden ${caption ? "rounded-[7px]" : ""}`}
        style={{
          width: layout.size.width,
          height: layout.size.height,
        }}
      >
        {layout.items.map((item, i) => {
          const msg = messages[i];
          if (!msg) return null;
          return (
            <MosaicCell
              key={msg.message_id}
              msg={msg}
              frame={item.frame}
              radius={mosaicCornerRadii(item.position, 16)}
              onOpen={() => openAt(i)}
            />
          );
        })}

        <div className="pointer-events-none absolute bottom-1.5 right-1.5 flex items-center gap-0.5 rounded-full bg-black/45 px-1.5 py-0.5 text-[10px] text-white">
          <span>{formatTimeOnly(last.createdAt, { hour12 })}</span>
          {isOwn ? (
            <LiaCheckDoubleSolid
              className={`h-3.5 w-3.5 ${
                last.message_seen_status === "seen"
                  ? "text-[#54E0FF]"
                  : "text-white/80"
              }`}
            />
          ) : null}
        </div>
      </div>

      {caption ? (
        <div
          className={`whitespace-pre-wrap break-words px-2 py-1.5 text-sm ${
            theme === "dark" ? "text-white" : "text-darkText"
          }`}
          style={{ overflowWrap: "anywhere" }}
        >
          {caption}
        </div>
      ) : null}
    </div>
  );
}
